// [2026-09-07] ADDED — what the signed-in member is allowed to do, in one place.
//
// WHY A FILE AND NOT AN INLINE CHECK. Two screens ask the same question — the shell, to decide
// whether to draw the "API Keys" tab and the header's "Create API Key +", and PlatformApp, to
// decide what to render if someone deep-links `?view=keys`. Written twice they drift, and the way
// they drift is silent: the tab disappears while the deep link still opens a screen whose every
// button 403s.
//
// THIS IS NOT THE AUTHORISATION. Nothing here protects anything — the browser is the caller's
// machine and every line of it is editable. The server decides, on every request, with
// require_role() reading the role out of the database (app/modules/me/route.py). This exists so we
// stop OFFERING a control whose only possible outcome is a 403, because a dead button is worse than
// an absent one: the person cannot tell whether they lack access or the product is broken.
//
// THE DEFAULT IS FULL ACCESS, AND THAT IS DELIBERATE.
//
// `role` arrives on GET /me, which only started returning it in the same change as this file. A
// browser holding this build against an older backend — the ordinary state of the world for the
// minutes between two deployments, and the permanent state of any environment where only the
// frontend was shipped — sees `undefined`. Defaulting that to "viewer" would hide the API Keys
// screen from every customer, including the owner, until the backend caught up: a self-inflicted
// outage on the screen a customer needs to integrate at all.
//
// Defaulting to full access instead means the worst case is that we briefly draw a button that the
// server refuses. That is the failure we can afford, and it is the same direction require_role
// itself fails when membership is switched off (it returns early and allows). The UI agreeing with
// the server is the property that matters.

export const ROLE_VIEWER = 'viewer'
export const ROLE_ADMIN = 'admin'
export const ROLE_OWNER = 'owner'

// Matches app/core/members.py:64 (_RANK). An unknown role scores 0 and is caught by roleOf's
// fallback below rather than by silently ranking bottom here.
const RANK = { [ROLE_VIEWER]: 1, [ROLE_ADMIN]: 2, [ROLE_OWNER]: 3 }

/** The role on a /me response, or ROLE_OWNER when the backend did not send one. See above. */
export function roleOf(session) {
  const role = session && session.role
  return RANK[role] ? role : ROLE_OWNER
}

/** True when this person's role is at least `required`. Mirrors members.at_least. */
export function atLeast(session, required) {
  return (RANK[roleOf(session)] || 0) >= (RANK[required] || 0)
}

// The one permission this file currently decides.
//
// Creating, revoking and deleting a key are all gated at ROLE_ADMIN on the server, so the whole
// screen is offered on that same line — a viewer opening a key list where every action is missing
// learns less than not being offered the screen at all. LISTING keys is not gated server-side (it
// returns prefixes and labels, never a secret) and the dashboard's key tile still reads it, which
// is why a viewer's Dashboard is unaffected.
export const canManageKeys = (session) => atLeast(session, ROLE_ADMIN)
